<script lang="ts">
/** One tickable row. `key` is what comes back on `toggle`. */
export interface GaiaCheckMenuOption {
  key: string;
  label: string;
  checked: boolean;
}
</script>

<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontSize, gaiaNeutral, gaiaShadow } from '../themes/tokens';

const props = defineProps({
  options: {
    type: Array as PropType<GaiaCheckMenuOption[]>,
    required: true,
  },
  /** Faint caps label above the list. Omit for a bare list. */
  title: {
    type: String,
    default: undefined,
  },
  confirmLabel: {
    type: String,
    default: 'Save',
  },
  cancelLabel: {
    type: String,
    default: 'Cancel',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: '208px',
  },
});

const emit = defineEmits<{ toggle: [key: string]; cancel: []; confirm: [] }>();

const rowSize = gaiaFontSize.dense;
const text = gaiaNeutral.text;
const ink = gaiaNeutral.ink;
const hover = gaiaNeutral.surfaceHover;
const divider = gaiaNeutral.borderSubtle;
const surface = gaiaNeutral.surface;
const menuShadow = gaiaShadow.menu;
</script>

<template>
  <!-- A short checkbox list that commits on Save — the confirming sibling of
       GaiaColumnsMenu, which applies each tick immediately. Used behind the
       ✎ on a share row to re-grant permissions. Host it in a GaiaPopover:
       this is only the menu body. -->
  <div class="gaia-check-menu" :style="{ width: props.width }">
    <gaia-text
      v-if="props.title"
      variant="label"
      size="nano"
      tone="faint"
      :style="{ padding: '5px 10px 6px' }"
    >
      {{ props.title }}
    </gaia-text>

    <!-- The whole row is the hit target, so the checkbox is decoration and
         takes itself out of the tab order and the pointer path. -->
    <button
      v-for="option in props.options"
      :key="option.key"
      type="button"
      class="row"
      @click="emit('toggle', option.key)"
    >
      <gaia-checkbox
        :checked="option.checked"
        size="small"
        tabindex="-1"
        style="pointer-events: none"
      />
      <span class="label">{{ option.label }}</span>
    </button>

    <div class="divider" />

    <n-flex justify="end" :size="8" :wrap="false" :style="{ padding: '0 4px 2px' }">
      <gaia-button size="tiny" @click="emit('cancel')">{{ props.cancelLabel }}</gaia-button>
      <gaia-button type="primary" size="tiny" :loading="props.loading" @click="emit('confirm')">
        {{ props.confirmLabel }}
      </gaia-button>
    </n-flex>
  </div>
</template>

<style scoped>
/* GaiaPopover is `raw`, so the menu supplies its own surface. */
.gaia-check-menu {
  display: flex;
  flex-direction: column;
  padding: 6px;
  border-radius: 10px;
  background: v-bind(surface);
  box-shadow: v-bind(menuShadow);
}

/* Each entry is a full-width <button> with a :hover, so its colours come
   through v-bind rather than a GaiaText prop. */
.row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 7px;
  background: none;
  font-family: inherit;
  font-size: v-bind(rowSize);
  color: v-bind(text);
  text-align: left;
  cursor: pointer;
}

.row:hover {
  background: v-bind(hover);
  color: v-bind(ink);
}

.label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.divider {
  height: 1px;
  margin: 5px 4px 8px;
  background: v-bind(divider);
}
</style>
